import "./PhaseSelector.css";

import { useContext } from "react";

import {
  CompletedCircleGreenIcon,
  InProgressCircleGreenIcon,
  NotStartedCircleGreyIcon,
} from "../../../../../../../assets";
import { ProjectPhase } from "../../../../../../../constants/project-phase";
import { ProjectDates, ProjectPhaseStatus } from "../../../../../../../models";
import { logError } from "../../../../../../../service/error";
import { GetTaskBoardResponse } from "../../../../../../../service/query";
import { capitalize } from "../../../../../../../utils";
import { Button, Toast } from "../../../../../../../widget";
import { TasksBoardContext } from "../../TasksBoardContext";

const calculateIcon = (
  status: string | undefined,
  phase: ProjectPhase,
  tasksBoard: GetTaskBoardResponse | undefined
): JSX.Element => {
  if (status === ProjectPhaseStatus.COMPLETED) {
    return <CompletedCircleGreenIcon />;
  }
  if (status === ProjectPhaseStatus.IN_PROGRESS) {
    const phaseSteps = tasksBoard?.results[0]?.steps.filter((step) => step.phase === phase);
    if (phaseSteps?.some((step) => !step.isComplete)) {
      return <InProgressCircleGreenIcon />;
    }
    return <NotStartedCircleGreyIcon className="CompletedStepsCircle" />;
  }
  return <NotStartedCircleGreyIcon className="NotStartedCircle" />;
};

const calculateDivider = (status: string | undefined): JSX.Element => {
  return (
    <div
      className={`PhaseDivider ${status === ProjectPhaseStatus.COMPLETED || status === ProjectPhaseStatus.IN_PROGRESS ? "Active" : ""}`}
    />
  );
};

const getPhaseIcon = (
  phase: ProjectPhase,
  tasksBoard: GetTaskBoardResponse | undefined,
  projectDates: ProjectDates | undefined
): JSX.Element => {
  switch (phase) {
    case ProjectPhase.feasibility:
      return calculateIcon(projectDates?.feasibility?.status, ProjectPhase.feasibility, tasksBoard);
    case ProjectPhase.design:
      return (
        <>
          {calculateDivider(projectDates?.design?.status)}
          {calculateIcon(projectDates?.design?.status, ProjectPhase.design, tasksBoard)}
        </>
      );
    case ProjectPhase.implementation:
      return (
        <>
          {calculateDivider(projectDates?.implementation?.status)}
          {calculateIcon(projectDates?.implementation?.status, ProjectPhase.implementation, tasksBoard)}
        </>
      );
    case ProjectPhase.ongoingMonitoring:
      return (
        <>
          {calculateDivider(projectDates?.ongoingMonitoring?.overallStatus)}
          {calculateIcon(projectDates?.ongoingMonitoring?.overallStatus, ProjectPhase.ongoingMonitoring, tasksBoard)}
        </>
      );
    default:
      logError({ error: `Invalid project phase: ${phase}` });
      Toast.error({ message: `Invalid project phase: ${phase}` });
      return <NotStartedCircleGreyIcon className="NotStartedCircle" />;
  }
};

export const PhaseSelector = (): JSX.Element => {
  const { currentPhase, tasksBoard, projectDates, handlePhaseChange } = useContext(TasksBoardContext);

  return (
    <div className="ProjectBoardPhaseSelectorContainer">
      <h3>Select phase</h3>
      <div className="ProjectBoardPhaseButtonsContainer">
        {Object.values(ProjectPhase).map((phase) => (
          <div className="ProjectBoardPhaseButton" key={phase}>
            {getPhaseIcon(phase, tasksBoard, projectDates?.dates)}
            {(phase as ProjectPhase) === currentPhase ? (
              <h3>{capitalize(phase)}</h3>
            ) : (
              <Button
                text={capitalize(phase)}
                variant="secondary"
                textUnderline
                hasNoPadding
                fontWeight="light"
                size="small"
                onClick={() => handlePhaseChange(phase as ProjectPhase)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
